// Stable per-visitor identifier used to correlate Lead Forensics page-view
// events (sent as `trk_guid` to LF's capture endpoint) with our own abandoned
// cart tracking. LF's standard integration generates a fresh GUID per page
// view, so without this they have no way to group views from one visitor and
// the abandoned-cart record can't reference them either.
//
// Stored under marketing-consent gating only — generation never happens
// without an explicit `consent.marketing === true` from the caller.

export const LEAD_FORENSICS_STORAGE_KEY = "aov_lf_vid"

/**
 * Read the stored Lead Forensics visitor ID. Returns null if storage is
 * unavailable or the ID has not been generated yet. Safe to call on the server
 * (returns null there too).
 */
export function readLeadForensicsVisitorId(): string | null {
  if (typeof window === "undefined") return null
  try {
    return window.localStorage.getItem(LEAD_FORENSICS_STORAGE_KEY) || null
  } catch {
    return null
  }
}

/**
 * Read or create the stable Lead Forensics visitor ID. Generation uses the
 * native `crypto.randomUUID()` when available and falls back to the same
 * Math.random shape Lead Forensics' own inline tracker uses, so the wire
 * format remains compatible with their backend.
 */
export function ensureLeadForensicsVisitorId(): string | null {
  if (typeof window === "undefined") return null
  const existing = readLeadForensicsVisitorId()
  if (existing) return existing
  const next = generateGuid()
  try {
    window.localStorage.setItem(LEAD_FORENSICS_STORAGE_KEY, next)
  } catch {
    // Best-effort — if storage is blocked we still return the GUID so the
    // caller can use it for the current navigation, it just won't persist.
  }
  return next
}

/**
 * Remove the stored visitor ID. Called when the visitor withdraws marketing
 * consent — we must not retain identifiers we no longer have permission to
 * keep.
 */
export function clearLeadForensicsVisitorId(): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(LEAD_FORENSICS_STORAGE_KEY)
  } catch {
    // Same defensive posture as the setters — storage may be unavailable.
  }
}

function generateGuid(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID()
  }
  // Matches the v4 shape Lead Forensics' inline tracker uses verbatim.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}
